import { createServerFn } from '@tanstack/react-start'
import { getDb } from '#/db/client.server'
import { matches } from '#/db/schema'
import { eq } from 'drizzle-orm'
import type { MatchState } from '#/types/cricket'

/**
 * Server function to save or update a match in Cloudflare D1
 */
export const saveMatchFn = createServerFn({ method: 'POST' })
  .validator((match: MatchState) => match)
  .handler(async ({ data: match }) => {
    try {
      const db = getDb()
      const existing = await db
        .select({ id: matches.id })
        .from(matches)
        .where(eq(matches.id, match.id))
        .get()

      const matchData = {
        id: match.id,
        hostTeam: match.hostTeam,
        visitorTeam: match.visitorTeam,
        tossWinner: match.tossWinner,
        tossDecision: match.tossDecision,
        totalOvers: match.totalOvers,
        playersPerTeam: match.settings.playersPerTeam,
        currentInnings: match.currentInnings,
        status: match.status,
        targetRuns: match.targetRuns ?? null,
        viewToken: match.tokens.viewToken,
        editToken: match.tokens.editToken,
        stateJson: JSON.stringify(match),
      }

      if (existing) {
        await db.update(matches).set(matchData).where(eq(matches.id, match.id))
      } else {
        await db.insert(matches).values(matchData)
      }
      return { success: true }
    } catch (err: any) {
      console.error('Failed to save match to D1:', err)
      return { success: false, error: err.message }
    }
  })

/**
 * Server function to fetch a match by ID from Cloudflare D1 for view-only/spectator link
 */
export const getMatchFn = createServerFn({ method: 'GET' })
  .validator((params: { id: string }) => params)
  .handler(async ({ data: { id } }) => {
    try {
      const db = getDb()
      const row = await db
        .select()
        .from(matches)
        .where(eq(matches.id, id))
        .get()

      if (!row || !row.stateJson) {
        return { match: null }
      }
      const match = JSON.parse(row.stateJson) as MatchState
      return { match }
    } catch (err: any) {
      console.error('Failed to get match from D1:', err)
      return { match: null }
    }
  })
